/* `/attachments` — files a user sends with a message, and files an agent shared out of its
   sandbox. Upload first, then name the ids in `POST /runs` (`attachment_ids`). The bytes
   need the bearer, so a plain link can't fetch them: a download reads them, then saves.
   `backend/modules/attachments/README.md`. */
import { useMutation } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { fileKind, fileSize } from "@/shared/lib/files";

import type { Attachment, ChatFile } from "@/shared/types";

export const attachmentKeys = {
  all: ["attachments"] as const,
  bytes: (id: string) => [...attachmentKeys.all, id, "bytes"] as const,
};

/* ------------------------------ endpoints -------------------------------- */

/** At most 10 MB; until a run names it, it belongs to no conversation. */
export async function uploadAttachment(file: File): Promise<ChatFile> {
  const form = new FormData();
  form.append("file", file);
  return unwrap(await client.post<Envelope<ChatFile>>("/attachments", form));
}

/** A chat file's bytes, read with the bearer. */
export async function readAttachment(id: string): Promise<Blob> {
  return (await client.get<Blob>(`/attachments/${id}`, { responseType: "blob" })).data;
}

export async function downloadAttachment({ id, name }: { id: string; name: string }): Promise<void> {
  const url = URL.createObjectURL(await readAttachment(id));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/* -------------------------------- hooks ---------------------------------- */

export const useUploadAttachment = () => useMutation({ mutationFn: uploadAttachment });

export const useDownloadAttachment = () => useMutation({ mutationFn: downloadAttachment });

/* ------------------------- the wire → the chat ---------------------------- */

/** A chat file as the chip the transcript already knows how to draw. */
export const toAttachment = (file: ChatFile): Attachment => ({
  id: file.id,
  name: file.filename,
  size: fileSize(file.bytes),
  kind: fileKind(file.filename),
});
